import { config } from '../config/environment';
import { readSseStream } from '../utils/sseStreamReader';

export type CanvasAction =
  | 'UNDERSTAND_PROBLEM' | 'UNDERSTAND_TOPIC' | 'SOLVE_PROBLEM'
  // Spec 046 additions:
  | 'CHECK_ANSWER' | 'REVEAL_SOLUTION' | 'IMAGE_QUESTION';

export interface CanvasActionExtra {
  questionText?: string;
  attemptText?: string;
  imageBase64?: string;
}

export interface CanvasActionHandlers {
  onBlockStart: (blockId: string, blockType: string) => void;
  onBlockChunk: (blockId: string, text: string) => void;
  onBlockComplete: (
    blockId: string,
    persistedBlockId: number | null,
    narration: string,
    blockType: 'TEXT' | 'IMAGE' | 'CHART',
    attrs: Record<string, any>
  ) => void;
  onNarrationReady: (blockId: string, audioUrl: string) => void;
  /** verdict is set only for CHECK_ANSWER (spec 046 FR-013/FR-014). */
  onDone: (verdict?: 'CORRECT' | 'INCORRECT' | 'PARTIAL') => void;
  onError: (message: string) => void;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Triggers a paste-a-question guided action and streams the response. Returns an
 * AbortController the caller can use to stop generation mid-stream (FR-012, T046a).
 */
export function triggerCanvasAction(
  role: 'tutor' | 'student',
  courseId: number,
  nodeId: number,
  action: CanvasAction,
  selectedText: string,
  handlers: CanvasActionHandlers,
  extra?: CanvasActionExtra
): AbortController {
  const controller = new AbortController();

  fetch(`${config.coreApiUrl}/api/${role}/courses/${courseId}/nodes/${nodeId}/ai-canvas-action`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ action, selectedText, ...extra }),
    signal: controller.signal,
  })
    .then((response) =>
      readSseStream(response, {
        onEvent: ({ event, data }) => {
          switch (event) {
            case 'block_start':
              handlers.onBlockStart(data.blockId, data.blockType);
              break;
            case 'block_chunk':
              handlers.onBlockChunk(data.blockId, data.text);
              break;
            case 'block_complete':
              handlers.onBlockComplete(data.blockId, data.persistedBlockId, data.narration, data.blockType || 'TEXT', data.attrs || {});
              break;
            case 'narration_ready':
              handlers.onNarrationReady(data.blockId, data.audioUrl);
              break;
            case 'done':
              handlers.onDone(data?.verdict);
              break;
            case 'error':
              handlers.onError(data.message || 'Generation failed — please try again.');
              break;
          }
        },
        onError: (err) => {
          if (controller.signal.aborted) return; // user-initiated stop, not a real error
          handlers.onError('Connection lost — please try again.');
          void err;
        },
      })
    )
    .catch((err) => {
      if (controller.signal.aborted) return;
      handlers.onError('Connection lost — please try again.');
      void err;
    });

  return controller;
}
